import { localize } from "client_legacy/ndaw/culture";
import {
	AvailableNotificationViewModel,
	NotificationKind,
	NotificationViewModel
} from "client/Api/Notifications/notifications";
import {
	muteActivityKind,
	muteAnnouncementReactions,
	muteCommunityMembership,
	muteCommunityReactions,
	muteCustomActivityTag,
	muteCustomActivityWithoutTag,
	muteEventReactions,
	muteKnowledgeItemReactions,
	muteNewApps
} from "./Settings/NotificationSettingsDialog";
import { markAsOrdered, OrderedReadonlyArray } from "./store";

export type ActivityStackState =
	| "collapsed"
	| "expanded"
	| "expanding";

type ActivityStackOptions = {
	id: string;
	title: string;
	muteStack(): Promise<void>;
};

export interface IActivityStack {
	id: string;
	title: string;
	activities: OrderedReadonlyArray<AvailableNotificationViewModel>;
	muteStack(): Promise<void>;
}

interface IActivityStackBuilder {
	doesMatch(activity: AvailableNotificationViewModel): boolean;
	add(activity: AvailableNotificationViewModel): void;
	getResult(): IActivityStack;
}

interface IActivityStackProvider {
	tryGet(activity: AvailableNotificationViewModel): TryResult<IActivityStackBuilder>;
}

abstract class BaseActivityStackBuilder implements IActivityStackBuilder {
	private readonly _options: ActivityStackOptions;
	private readonly _activities: AvailableNotificationViewModel[];

	constructor (options: ActivityStackOptions) {
		this._options = options;
		this._activities = [];
	}

	public abstract doesMatch(activity: NotificationViewModel): boolean;

	public add(activity: AvailableNotificationViewModel): void {
		this._activities.push(activity);
	}

	public getResult(): IActivityStack {
		const {
			id,
			title,
			muteStack
		} = this._options;

		return {
			id,
			title,
			activities: markAsOrdered({ alreadyOrderedItems: this._activities }),
			muteStack
		};
	}
}

abstract class StaticActivityStackProvider extends BaseActivityStackBuilder implements IActivityStackProvider {
	public tryGet(activity: NotificationViewModel): TryResult<IActivityStackBuilder> {
		if (this.doesMatch(activity)) {
			return {
				wasSuccessful: true,
				result: this
			};
		}
		return { wasSuccessful: false };
	}
}

class EmailActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "ed53b38c-d067-42f8-a229-ecb57d0f32f3",
			title: localize("_Emails"),
			muteStack: () => muteActivityKind(NotificationKind.NewEmail)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.NewEmail;
	}
}

class AnnouncementActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "ed53b38c-d067-42f8-a229-ecb57d0f32f4",
			title: localize("_Announcements"),
			muteStack: () => muteActivityKind(NotificationKind.Announcement)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.Announcement;
	}
}

class CommunityPostActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "cb027fe8-8f45-4d77-9341-4dd7eede6652",
			title: localize("_CommunityPosts"),
			muteStack: () => muteActivityKind(NotificationKind.CommunityPost)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.CommunityPost;
	}
}

class CommunityPostCommentActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "56825a73-3bf2-4b7b-b739-d256b286991d",
			title: localize("_CommunityComments"),
			muteStack: () => muteActivityKind(NotificationKind.CommunityPostComment)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.CommunityPostComment;
	}
}

class CommunityPostCommentMentionActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "a01604c6-7d10-4888-815a-3480629e7056",
			title: localize("_CommunityMentions"),
			muteStack: () => muteActivityKind(NotificationKind.CommunityPostCommentMention)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.CommunityPostCommentMention;
	}
}

class CommunityActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "b3f8e92a-1c4d-4a7e-9f2b-5d6e8a1c0b4f",
			title: localize("_Communities"),
			muteStack: () => muteActivityKind(NotificationKind.Community)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.Community;
	}
}

class CommunityMembershipActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "5294930f-e835-4964-9050-0c8ae0a1fc7d",
			title: localize("_CommunityMembership"),
			muteStack: () => muteCommunityMembership()
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.CommunityMembership
			|| activity.kind === NotificationKind.CommunityMembershipRequestDeclined;
	}
}

class CommunityMembershipRequestActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "e73d42b0-3d29-40cf-91d4-7b868df1f205",
			title: localize("_MembershipRequests"),
			muteStack: () => muteActivityKind(NotificationKind.CommunityMembershipRequested)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.CommunityMembershipRequested;
	}
}

class CommunityReactionsActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "8644ffa1-6455-47ea-a6a9-789ae2358248",
			title: localize("_CommunityReactions"),
			muteStack: () => muteCommunityReactions()
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.CommunityPostReaction
			|| activity.kind === NotificationKind.CommunityPostCommentReaction;
	}
}

class CrossStoreActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "ed53b38c-d067-42f8-a229-ecb57d0f32f5",
			title: localize("_MovedCopiedItems"),
			muteStack: () => muteActivityKind(NotificationKind.CrossStoreOperation)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.CrossStoreOperation;
	}
}

class NewAppsActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "ed53b38c-d067-42f8-a229-ecb57d0f32f6",
			title: localize("_NewAvailableApps"),
			muteStack: () => muteNewApps()
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.AppAccess || activity.kind === NotificationKind.NewMainAppPromotion;
	}
}

class MaintenanceActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "ed53b38c-d067-42f8-a229-ecb57d0f32f7",
			title: localize("_PlannedMaintenances"),
			muteStack: () => muteActivityKind(NotificationKind.Maintenance)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.Maintenance;
	}
}

class UserRequestActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "3d968444-e044-43f5-9d61-f042510ad3e3",
			title: localize("_UserRequests"),
			muteStack: () => muteActivityKind(NotificationKind.UserRequest)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.UserRequest;
	}
}

class BirthdayActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "9a26c7d7-70d7-4cc8-8df3-3e0d7f90821b",
			title: localize("_Birthdays"),
			muteStack: () => muteActivityKind(NotificationKind.Birthday)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.Birthday;
	}
}

class BirthdayWishActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "bdeef5d1-bbd4-4fc8-b2e9-0cbd4ab0217b",
			title: localize("_BirthdayWishes"),
			muteStack: () => muteActivityKind(NotificationKind.BirthdayWish)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.BirthdayWish;
	}
}

class BirthdayWishReactionActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "1cd6ab22-990e-4bc6-bf40-f13a248e1a0f",
			title: localize("_BirthdayWishReactions"),
			muteStack: () => muteActivityKind(NotificationKind.BirthdayWishReaction)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.BirthdayWishReaction;
	}
}

class AnnouncementCommentsActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "5a1fcffe-d09b-4abf-8e0e-5f7ed1879cfc",
			title: localize("_AnnouncementComments"),
			muteStack: () => muteActivityKind(NotificationKind.AnnouncementComment)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.AnnouncementComment;
	}
}

class AnnouncementReactionsActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "e241e3a1-daec-4075-95ce-f116decc215f",
			title: localize("_AnnouncementReactions"),
			muteStack: () => muteAnnouncementReactions()
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.AnnouncementCommentReaction
			|| activity.kind === NotificationKind.AnnouncementReaction;
	}
}

class AnnouncementCommentMentionActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "a4dec196-9607-40cc-9e78-afe715a83757",
			title: localize("_AnnouncementMentions"),
			muteStack: () => muteActivityKind(NotificationKind.AnnouncementCommentMention)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.AnnouncementCommentMention;
	}
}

class EventAttendanceActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "79650e09-ec2d-4882-89ea-904bcb2742dc",
			title: localize("_EventAttendance"),
			muteStack: () => muteActivityKind(NotificationKind.EventAttendance)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.EventAttendance;
	}
}

class KnowledgeItemActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "8d152846-3b7b-4b6c-b10b-a6845cd36340",
			title: localize("_KnowledgeArticles"),
			muteStack: () => muteActivityKind(NotificationKind.KnowledgeItem)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.KnowledgeItem;
	}
}

class KnowledgeItemCommentActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "c2f8ddac-957a-4412-a21a-4618cae20611",
			title: localize("_KnowledgeArticleComments"),
			muteStack: () => muteActivityKind(NotificationKind.KnowledgeItemComment)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.KnowledgeItemComment;
	}
}

class KnowledgeItemCommentMentionActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "e0f4fbab-4e5b-4319-85b1-7fe0d1a0d86b",
			title: localize("_KnowledgeArticleMentions"),
			muteStack: () => muteActivityKind(NotificationKind.KnowledgeItemCommentMention)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.KnowledgeItemCommentMention;
	}
}

class KnowledgeItemReactionActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "afa7c272-7c75-488c-9971-45d6d74f8b13",
			title: localize("_KnowledgeArticleReactions"),
			muteStack: () => muteKnowledgeItemReactions()
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.KnowledgeItemReaction
			|| activity.kind === NotificationKind.KnowledgeItemCommentReaction;
	}
}

class EventActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "78cb451b-01d2-40a1-8976-5fd71d46a465",
			title: localize("_Events"),
			muteStack: () => muteActivityKind(NotificationKind.Event)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.Event;
	}
}

class EventCommentActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "63e3c5f3-7a30-47c0-95af-ff5d07942410",
			title: localize("_EventComments"),
			muteStack: () => muteActivityKind(NotificationKind.EventComment)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.EventComment;
	}
}

class EventCommentMentionActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "5f02493c-8266-4b5f-92d8-27ec2ba171aa",
			title: localize("_EventMentions"),
			muteStack: () => muteActivityKind(NotificationKind.EventCommentMention)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.EventCommentMention;
	}
}

class EventReactionActivityStackBuilder extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "bdf75a14-fa2c-47fc-8d4b-be99f4ab45c6",
			title: localize("_EventReactions"),
			muteStack: () => muteEventReactions()
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.EventReaction
			|| activity.kind === NotificationKind.EventCommentReaction;
	}
}

class CustomActivityStackBuilder extends BaseActivityStackBuilder {
	private readonly _tagId: string;
	constructor(initialActivityTag: string | nil) {
		const { id, title } = CustomActivityStackBuilder.getTagInfo(initialActivityTag);
		super({
			id: `b2977e55-ecb0-44db-a66f-b9cd79e412cf__${id}`,
			title,
			muteStack: () => initialActivityTag
				? muteCustomActivityTag(initialActivityTag)
				: muteCustomActivityWithoutTag()
		});

		this._tagId = id;
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		if (activity.kind !== NotificationKind.Custom) {
			return false;
		}
		const { id } = CustomActivityStackBuilder.getTagInfo(activity.data.tag);
		return id === this._tagId;
	}

	private static getTagInfo(tag: string | nil): { id: string; title: string; } {
		if (!tag) {
			return {
				id: "OTHER",
				title: localize("_Other")
			};
		}

		return {
			id: "TAG_" + normalizeCustomActivityTag(tag),
			title: tag
		};
	}
}

class CustomActivityStackProvider implements IActivityStackProvider {
	public tryGet(activity: AvailableNotificationViewModel): TryResult<IActivityStackBuilder> {
		if (activity.kind !== NotificationKind.Custom) {
			return { wasSuccessful: false };
		}

		return {
			wasSuccessful: true,
			result: new CustomActivityStackBuilder(activity.data.tag)
		};
	}
}

class DocumentSharedActivityStackProvider extends StaticActivityStackProvider {
	constructor() {
		super({
			id: "5620e3dd-2c5f-45dd-b3ba-5386ba8642a5",
			title: localize("_SharedItems"),
			muteStack: () => muteActivityKind(NotificationKind.DocumentShared)
		});
	}

	public doesMatch(activity: NotificationViewModel): boolean {
		return activity.kind === NotificationKind.DocumentShared;
	}
}

export function groupActivities(activities: OrderedReadonlyArray<AvailableNotificationViewModel>)
	: OrderedReadonlyArray<AvailableNotificationViewModel|IActivityStack> {
	const providers: ReadonlyArray<IActivityStackProvider> = [
		new EmailActivityStackBuilder(),
		new AnnouncementActivityStackBuilder(),
		new AnnouncementCommentsActivityStackBuilder(),
		new CrossStoreActivityStackBuilder(),
		new NewAppsActivityStackBuilder(),
		new MaintenanceActivityStackBuilder(),
		new UserRequestActivityStackBuilder(),
		new BirthdayActivityStackBuilder(),
		new BirthdayWishActivityStackBuilder(),
		new BirthdayWishReactionActivityStackBuilder(),
		new CustomActivityStackProvider(),
		new DocumentSharedActivityStackProvider(),
		new KnowledgeItemActivityStackBuilder(),
		new AnnouncementReactionsActivityStackBuilder(),
		new EventActivityStackBuilder(),
		new EventAttendanceActivityStackBuilder(),
		new AnnouncementCommentMentionActivityStackBuilder(),
		new CommunityActivityStackBuilder(),
		new CommunityMembershipActivityStackBuilder(),
		new CommunityMembershipRequestActivityStackBuilder(),
		new CommunityPostActivityStackBuilder(),
		new CommunityPostCommentActivityStackBuilder(),
		new CommunityPostCommentMentionActivityStackBuilder(),
		new CommunityReactionsActivityStackBuilder(),
		new EventCommentActivityStackBuilder(),
		new EventCommentMentionActivityStackBuilder(),
		new EventReactionActivityStackBuilder(),
		new KnowledgeItemCommentActivityStackBuilder(),
		new KnowledgeItemCommentMentionActivityStackBuilder(),
		new KnowledgeItemReactionActivityStackBuilder()
	];

	const builders: IActivityStackBuilder[] = [];
	const preresult: (IActivityStackBuilder|AvailableNotificationViewModel)[] = [];

	const tryGetMatchingBuilder = (activity: AvailableNotificationViewModel): TryResult<IActivityStackBuilder> => {
		for (const builder of builders) {
			if (builder.doesMatch(activity)) {
				return {
					wasSuccessful: true,
					result: builder
				};
			}
		}

		for (const provider of providers) {
			const providerResult: TryResult<IActivityStackBuilder> = provider.tryGet(activity);
			if (providerResult.wasSuccessful) {
				const builder: IActivityStackBuilder = providerResult.result;
				builders.push(builder);
				preresult.push(builder);

				return {
					wasSuccessful: true,
					result: builder
				};
			}
		}

		return {
			wasSuccessful: false
		};
	};

	for (const activity of activities) {
		const builderResult: TryResult<IActivityStackBuilder> = tryGetMatchingBuilder(activity);

		if (builderResult.wasSuccessful) {
			builderResult.result.add(activity);
		} else {
			preresult.push(activity);
		}
	}

	const result: (AvailableNotificationViewModel|IActivityStack)[] = preresult.map(
		p => isStackBuilder(p)
			? p.getResult()
			: p
	);

	// NOTE: this method preserves the original order
	// Stacks get in the result as first activity from this stack appears
	return markAsOrdered({ alreadyOrderedItems: result });
}

export function normalizeCustomActivityTag(tag: string): string {
	return tag.trim()
		.toUpperCase();
}

function isStackBuilder(listItem: IActivityStackBuilder|NotificationViewModel): listItem is IActivityStackBuilder {
	return typeof (listItem as IActivityStackBuilder).getResult === "function";
}